// Обратная связь из справки (/faq → «Не нашли ответ?»).
//
// Здесь — pure-функции (валидация входа + форматирование уведомления
// разработчику в Telegram), тестируемые без Supabase/HTTP. Доставка
// (sendMessage), сессия и создание аккаунта живут в web-роуте /api/feedback.
//
// Сообщение — это user-input, поэтому уведомление формируем PLAIN TEXT без
// Markdown: спецсимволы в тексте (_ * [ ] ` и пр.) не должны ломать парсер
// Telegram (feedback_no_markdown_with_user_strings).

import { normalizeEmail } from './emailAuth';

/** Потолок длины сообщения — защита от мусора/раздувания. */
export const FEEDBACK_MAX_LEN = 2000;

export type FeedbackInput = {
  message: string;
};

export type FeedbackValidation =
  | { ok: true; value: FeedbackInput }
  | { ok: false; error: string };

function validateMessage(raw: unknown): { ok: true; message: string } | { ok: false; error: string } {
  const message = typeof raw === 'string' ? raw.trim() : '';
  if (message.length === 0) {
    return { ok: false, error: 'Напишите сообщение.' };
  }
  if (message.length > FEEDBACK_MAX_LEN) {
    return { ok: false, error: `Сообщение слишком длинное (до ${FEEDBACK_MAX_LEN} символов).` };
  }
  return { ok: true, message };
}

/** Вход залогиненного: только текст (аккаунт и канал известны из сессии). */
export function validateFeedback(raw: { message?: unknown }): FeedbackValidation {
  const r = validateMessage(raw.message);
  return r.ok ? { ok: true, value: { message: r.message } } : { ok: false, error: r.error };
}

export type AnonFeedbackInput = {
  email: string;
  message: string;
};

export type AnonFeedbackValidation =
  | { ok: true; value: AnonFeedbackInput }
  | { ok: false; error: string };

/**
 * Вход анонима: email + текст. Отправка заводит аккаунт по email (мы отвечаем
 * только зарегистрированным), поэтому email обязателен и проверяется по формату.
 */
export function validateAnonFeedback(raw: { email?: unknown; message?: unknown }): AnonFeedbackValidation {
  const email = normalizeEmail(raw.email);
  if (!email) {
    return { ok: false, error: 'Укажите корректный email.' };
  }
  const r = validateMessage(raw.message);
  if (!r.ok) return { ok: false, error: r.error };
  return { ok: true, value: { email, message: r.message } };
}

/**
 * Текст уведомления разработчику в Telegram. Plain text.
 * Включает контакты отправителя (telegram_id / vk_id / email), чтобы можно
 * было ответить, и userId как опору для поиска в БД.
 */
export function formatFeedbackNotification(req: {
  message: string;
  userId: string;
  telegramId?: number;
  vkId?: number;
  email?: string;
  isNewAccount?: boolean;
}): string {
  const header = req.isNewAccount ? '💬 Обращение из справки (новый аккаунт)' : '💬 Обращение из справки';
  const lines = [header, '', req.message, ''];

  const reach: string[] = [];
  if (req.telegramId && req.telegramId > 0) reach.push(`TG id ${req.telegramId}`);
  if (req.vkId) reach.push(`VK id ${req.vkId}`);
  if (req.email) reach.push(`email ${req.email}`);
  lines.push(reach.length > 0 ? `Связаться: ${reach.join(' · ')}` : 'Связаться: только веб-аккаунт');
  lines.push(`User: ${req.userId}`);

  return lines.join('\n');
}
